import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Badge } from "../../components/ui/badge";
import { Separator } from "../../components/ui/separator";
import { DisbursementTranche, LenderTag } from "../../types/deals";
import { 
  Building2, 
  Calendar, 
  Banknote, 
  CheckCircle2, 
  Clock, 
  AlertTriangle,
  CreditCard,
  Timer,
  User,
  Percent,
  FileText,
  IndianRupee,
  Repeat,
  CalendarDays,
  TrendingDown,
  Calculator,
  CircleCheck,
  CircleX,
  CircleAlert,
  Shield
} from "lucide-react";
import { format, parseISO, differenceInDays, addMonths, isAfter } from "date-fns";

interface ViewDisbursementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  disbursement: DisbursementTranche | null;
  lender: LenderTag | undefined;
}

export function ViewDisbursementDialog({ 
  open, 
  onOpenChange, 
  disbursement, 
  lender 
}: ViewDisbursementDialogProps) {
  if (!disbursement) return null;

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(amount);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "—";
    try {
      return format(parseISO(dateStr), "dd MMM yyyy");
    } catch {
      return dateStr;
    }
  };

  // Calculate status
  const today = new Date();
  const dueDate = parseISO(disbursement?.dueDate ?? "");
  const daysUntilDue = differenceInDays(dueDate, today);
  
  const maturedOn = disbursement.maturedOn 
    ? parseISO(disbursement?.maturedOn ?? "")
    : disbursement.commencementDate 
      ? addMonths(parseISO(disbursement?.commencementDate ?? ""), disbursement?.numberOfRentals || 12)
      : null;
  
  const isMatured = maturedOn && isAfter(today, maturedOn);
  const isActive = !isMatured;

  const getStatusInfo = () => {
    if (disbursement.status === "Rental Paid") {
      return { label: "Rental Paid", variant: "default" as const, color: "text-green-600" };
    }
    if (disbursement.status === "Outstanding") {
      return { label: "Outstanding", variant: "outline" as const, color: "text-amber-600" };
    }
    if (daysUntilDue < 0) {
      return { label: `Overdue by ${Math.abs(daysUntilDue)} days`, variant: "destructive" as const, color: "text-red-600" };
    }
    if (isMatured) {
      return { label: "Matured", variant: "secondary" as const, color: "text-gray-600" };
    }
    return { label: "Active", variant: "default" as const, color: "text-green-600" };
  };

  const statusInfo = getStatusInfo();

  // Calculate remaining rentals
  const paidCount = disbursement.paidCount ?? 0;
  const totalRentals = disbursement.numberOfRentals || 12;
  const remainingRentals = disbursement.remainingCount ?? (totalRentals - paidCount);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-xl overflow-y-auto">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            Disbursement Details
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-6">
          {/* Lender Info */}
          <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/50">
            <div className="bg-primary/10 flex items-center justify-center">
              {lender?.institutionAvatar ? (
                <img
                  src={lender.institutionAvatar.startsWith("http") ? lender.institutionAvatar : `${import.meta.env.VITE_API_BASE_URL}/${lender.institutionAvatar}`}
                  alt={lender.institutionName}
                  className="w-[80px] h-[40px] rounded-full object-cover"
                />
              ) : (
                <Building2 className="h-6 w-6 text-primary" />
              )}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">{lender?.institutionName ?? "Unknown Lender"}</h3>
              <p className="text-sm text-muted-foreground">{lender?.lenderName ?? ""}</p>
            </div>
            <div className="flex items-center gap-2">
              {isActive ? (
                <Badge variant="default" className="bg-green-600">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Active
                </Badge>
              ) : (
                <Badge variant="destructive">
                  <Clock className="h-3 w-3 mr-1" />
                  Matured
                </Badge>
              )}
            </div>
          </div>

          {/* Status Tags */}
          <div className="flex flex-wrap gap-2">
            {/* <Badge variant={statusInfo.variant} className="text-sm">
              {daysUntilDue < 0 && <AlertTriangle className="h-3 w-3 mr-1" />}
              {statusInfo.label}
            </Badge> */}
            {disbursement.noaNachActive && (
              <Badge variant="outline" className="text-sm border-green-500 text-green-600 dark:text-green-400">
                <CreditCard className="h-3 w-3 mr-1" />
                NACH Active
              </Badge>
            )}
            {!disbursement.noaNachActive && (
              <Badge variant="outline" className="text-sm border-red-500 text-red-600 dark:text-red-400">
                <CreditCard className="h-3 w-3 mr-1" />
                NACH Inactive
              </Badge>
            )}
          </div>

          <Separator />

          {/* Key Dates */}
          <div>
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              Key Dates
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 rounded-lg bg-muted/30">
                <div className="text-xs text-muted-foreground">Disbursed On</div>
                <div className="font-medium">{formatDate(disbursement.disbursedOn)}</div>
              </div>
              <div className="p-3 rounded-lg bg-muted/30">
                <div className="text-xs text-muted-foreground">Commencement Date</div>
                <div className="font-medium">{formatDate(disbursement.commencementDate)}</div>
              </div>
              <div className="p-3 rounded-lg bg-muted/30">
                <div className="text-xs text-muted-foreground">Matured On</div>
                <div className="font-medium">{formatDate(disbursement.maturedOn)}</div>
              </div>
              <div className={`p-3 rounded-lg ${daysUntilDue < 0 ? 'bg-red-50 dark:bg-red-950/30' : 'bg-muted/30'}`}>
                <div className="text-xs text-muted-foreground">Due Date</div>
                <div className={`font-medium ${daysUntilDue < 0 ? 'text-red-600' : ''}`}>{formatDate(disbursement?.dueDate)}</div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Financial Details */}
          <div>
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Banknote className="h-4 w-4 text-muted-foreground" />
              Financial Details
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 rounded-lg bg-primary/5">
                <div className="text-xs text-muted-foreground">Disbursed Amount</div>
                <div className="text-lg font-bold text-primary">{formatCurrency(disbursement.disbursedAmount)}</div>
              </div>
              {disbursement.dueAmount !== undefined && (
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20">
                  <div className="text-xs text-muted-foreground">Due Amount</div>
                  <div className="text-lg font-bold text-amber-600">{formatCurrency(disbursement.dueAmount)}</div>
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* Rental Structure - Updated Fields */}
          <div>
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Timer className="h-4 w-4 text-muted-foreground" />
              Rental Structure
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center p-2.5 rounded bg-muted/30">
                <span className="flex items-center gap-2 text-muted-foreground"><IndianRupee className="h-3.5 w-3.5" /> Rental Amount</span>
                <span className="font-medium">{formatCurrency(disbursement.monthlyRental)}</span>
              </div>
              <div className="flex justify-between items-center p-2.5 rounded bg-muted/30">
                <span className="flex items-center gap-2 text-muted-foreground"><Banknote className="h-3.5 w-3.5" /> Lender EMI</span>
                <span className="font-medium">{formatCurrency(disbursement.lenderPF || 0)}</span>
              </div>
              <div className="flex justify-between items-center p-2.5 rounded bg-muted/30">
                <span className="flex items-center gap-2 text-muted-foreground"><Repeat className="h-3.5 w-3.5" /> Frequency</span>
                <span className="font-medium">{disbursement.rentalFrequency ? disbursement.rentalFrequency.charAt(0).toUpperCase() + disbursement.rentalFrequency.slice(1) : "Monthly"}</span>
              </div>
              <div className="flex justify-between items-center p-2.5 rounded bg-muted/30">
                <span className="flex items-center gap-2 text-muted-foreground"><CalendarDays className="h-3.5 w-3.5" /> Tenure</span>
                <span className="font-medium">{disbursement.tenure ? disbursement.tenure : disbursement.numberOfRentals ? disbursement.numberOfRentals : "—"} months</span>
              </div>
              <div className="flex justify-between items-center p-2.5 rounded bg-muted/30">
                <span className="flex items-center gap-2 text-muted-foreground"><TrendingDown className="h-3.5 w-3.5" /> Discounting Factor</span>
                <span className="font-medium">{disbursement.interestRate || "—"}%</span>
              </div>
              <div className="flex justify-between items-center p-2.5 rounded bg-muted/30">
                <span className="flex items-center gap-2 text-muted-foreground"><Calculator className="h-3.5 w-3.5" /> Total EMI</span>
                <span className="font-medium">{totalRentals}</span>
              </div>
              <div className="flex justify-between items-center p-2.5 rounded bg-green-50 dark:bg-green-950/20">
                <span className="flex items-center gap-2 text-muted-foreground"><CircleCheck className="h-3.5 w-3.5 text-green-600" /> Paid EMI</span>
                <span className="font-medium text-green-600">{paidCount}</span>
              </div>
              <div className="flex justify-between items-center p-2.5 rounded bg-amber-50 dark:bg-amber-950/20">
                <span className="flex items-center gap-2 text-muted-foreground"><CircleX className="h-3.5 w-3.5 text-amber-600" /> Outstanding EMI</span>
                <span className="font-medium text-amber-600">{remainingRentals}</span>
              </div>
              <div className="flex justify-between items-center p-2.5 rounded bg-red-50 dark:bg-red-950/20">
                <span className="flex items-center gap-2 text-muted-foreground"><CircleAlert className="h-3.5 w-3.5 text-red-600" /> Overdue EMI</span>
                <span className="font-medium text-red-600">{disbursement.overdueDays && disbursement.overdueDays > 0 ? Math.ceil(disbursement.overdueDays / 30) : 0}</span>
              </div>
              <div className="flex justify-between items-center p-2.5 rounded bg-muted/30">
                <span className="flex items-center gap-2 text-muted-foreground"><Shield className="h-3.5 w-3.5" /> NACH Status</span>
                <span className={`font-medium ${disbursement.noaNachActive ? 'text-green-600' : 'text-muted-foreground'}`}>
                  {disbursement.noaNachActive ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
          </div>

          <Separator />

          {/* Additional Details */}
          <div>
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              Additional Details
            </h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex justify-between p-2 rounded bg-muted/30">
                <span className="text-muted-foreground">Funding Mode</span>
                <span className="font-medium">{disbursement.fundingMode ==='A' ? 'Pre GST' : disbursement.fundingMode ==='B' ? 'Post GST' : disbursement.fundingMode ==='C' ? 'POST TDS' : "—"}</span>
              </div>
              <div className="flex justify-between p-2 rounded bg-muted/30">
                <span className="text-muted-foreground">Schedule No.</span>
                <span className="font-medium">{'RS.No. ' + disbursement.rentalScheduleNumber}</span>
              </div>
            </div>
          </div>

          {/* Payout Details (for Hybrid) */}
          {(disbursement.partnerPayoutAmount || disbursement.customerPayoutAmount || disbursement.lenderPayoutAmount) && (
            <>
              <Separator />
              <div>
                <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Percent className="h-4 w-4 text-muted-foreground" />
                  Payout Details
                </h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 text-center">
                    <div className="text-xs text-muted-foreground">Customer Payout</div>
                    <div className="font-semibold text-blue-600">{formatCurrency(disbursement.customerPayoutAmount || 0)}</div>
                    {disbursement.customerPayoutPercent !== undefined && (
                      <div className="text-xs text-muted-foreground">({disbursement.customerPayoutPercent}%)</div>
                    )}
                  </div>
                  <div className="p-3 rounded-lg bg-green-50 dark:bg-green-950/20 text-center">
                    <div className="text-xs text-muted-foreground">Lender Payout</div>
                    <div className="font-semibold text-green-600">{formatCurrency(disbursement.lenderPayoutAmount || 0)}</div>
                    {disbursement.lenderPayoutPercent !== undefined && (
                      <div className="text-xs text-muted-foreground">({disbursement.lenderPayoutPercent}%)</div>
                    )}
                  </div>
                  <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 text-center">
                    <div className="text-xs text-muted-foreground">Partner Payout</div>
                    <div className="font-semibold text-amber-600">{formatCurrency(disbursement.partnerPayoutAmount || 0)}</div>
                    {disbursement.partnerPayoutPercent !== undefined && (
                      <div className="text-xs text-muted-foreground">({disbursement.partnerPayoutPercent}%)</div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          <Separator />

          {/* Created Info */}
          {/* <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <User className="h-4 w-4" />
              <span>Created by: {disbursement.createdBy || "—"}</span>
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              <span>Created at: {formatDate(disbursement.createdAt)}</span>
            </div>
          </div> */}
        </div>
      </SheetContent>
    </Sheet>
  );
}
